import { useState, useEffect, useCallback } from "react";
import { getData } from "../../../../client/api/rest";
import { StoryStructure, ValidationResult } from "common/build/api-parameters/story";

interface UseBranchedStoryResult {
    loading: boolean;
    structure: StoryStructure | null;
    validation: ValidationResult | null;
    error: string | null;
    refetch: () => void;
}

/**
 * Custom hook for managing branched story data and operations
 *
 * Provides loading state, story structure data, validation results,
 * and error handling for branched story operations.
 *
 * @param storyId - The ID of the branched story to fetch
 * @returns Object containing loading state, data, and refetch function
 *
 * @example
 * const { structure, loading, error, refetch } = useBranchedStory(123);
 */
export const useBranchedStory = (storyId?: number): UseBranchedStoryResult => {
    // State management following design guide patterns
    const [loading, setLoading] = useState(false);
    const [structure, setStructure] = useState<StoryStructure | null>(null);
    const [validation, setValidation] = useState<ValidationResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Memoized fetch function to prevent unnecessary re-renders
    const fetchStoryStructure = useCallback(async () => {
        if (!storyId) return;

        setLoading(true);
        setError(null);

        try {
            const response = await getData<StoryStructure>(`/branched-story/${storyId}/structure`);
            setStructure(response);

            // Also fetch validation if available
            try {
                const validationResponse = await getData<ValidationResult>(`/branched-story/${storyId}/validate`);
                setValidation(validationResponse);
            } catch (validationErr) {
                // Validation endpoint might not exist for all stories
                setValidation(null);
            }
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : "Failed to load story structure";
            setError(errorMessage);
            setStructure(null);
            setValidation(null);
        } finally {
            setLoading(false);
        }
    }, [storyId]);

    // Effect to fetch data when storyId changes
    useEffect(() => {
        fetchStoryStructure();
    }, [fetchStoryStructure]);

    // Clear data when storyId is removed
    useEffect(() => {
        if (!storyId) {
            setStructure(null);
            setValidation(null);
            setError(null);
            setLoading(false);
        }
    }, [storyId]);

    return {
        loading,
        structure,
        validation,
        error,
        refetch: fetchStoryStructure,
    };
};

// Display name for debugging
useBranchedStory.displayName = "useBranchedStory";
